import type { MutableRefObject } from "react";
import { useEffect, useRef } from "react";

type EscapeHandler = {
  ref: MutableRefObject<Callback>;
  ignoreWhenFocusedOnInput: boolean;
};

type Callback = (ev?: KeyboardEvent) => void | boolean;

let stack: Array<EscapeHandler> = [];

let attached = false;

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    const cb = stack[stack.length - 1];
    if (!cb?.ref?.current) return;

    const active = document.activeElement as HTMLElement | null;

    const skipEscapeHandler =
      active &&
      (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);

    const shouldSkip = skipEscapeHandler && cb.ignoreWhenFocusedOnInput;

    if (shouldSkip) return;

    // Unless callback returns false, remove it from the stack
    if (cb.ref.current(event) !== false) stack.pop();

    event.stopPropagation();
    event.preventDefault();
  }
};

export function useEscapeKey({
  callback,
  enabled,
  isEditing = false,
  ignoreWhenFocusedOnInput = false,
}: {
  callback: Callback;
  enabled: boolean;
  isEditing?: boolean;
  ignoreWhenFocusedOnInput?: boolean;
}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled || isEditing) return;

    stack.push({ ref: cbRef, ignoreWhenFocusedOnInput });

    return () => {
      stack = stack.filter((cb) => cb.ref !== cbRef);
    };
  }, [enabled, isEditing]);

  useEffect(() => {
    if (attached) return;

    window.addEventListener("keydown", handleKeyPress, { capture: true });
    attached = true;

    return () => {
      if (stack.length === 0) {
        window.removeEventListener("keydown", handleKeyPress, { capture: true });
        attached = false;
      }
    };
  }, []);
}
